/**
 * Phrases Learning Page
 * Display phrases organized by categories or grades
 */

import { useState } from 'react';
import { allPhrasesData as phrasesData, phraseCategories, Phrase } from '../data/phrases-complete';
import PhraseCard from '../components/vocabulary/PhraseCard';
import PhraseTestPage from './PhraseTestPage';
import { UserProgress } from '../services/storage';

type ViewMode = 'category' | 'grade' | 'all' | 'test';
type GradeLevel = '3' | '4' | '5' | '6' | '7' | '8' | '9';

interface PhrasesPageProps {
  user?: UserProgress;
}

export default function PhrasesPage({ user }: PhrasesPageProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('category');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedGrade, setSelectedGrade] = useState<GradeLevel>('3');
  const [learnedPhrases, setLearnedPhrases] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');

  const handleLearned = (phrase: Phrase) => {
    setLearnedPhrases(prev => {
      const newSet = new Set(prev);
      newSet.add(phrase.id);
      return newSet;
    });
  };

  const getFilteredPhrases = () => {
    let phrases = phrasesData;

    if (viewMode === 'category' && selectedCategory !== 'all') {
      phrases = phrases.filter(p => p.category === selectedCategory);
    } else if (viewMode === 'grade') {
      phrases = phrases.filter(p => p.grade === selectedGrade);
    }

    // Search filter (中英文模糊搜索)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      phrases = phrases.filter(p => {
        const matchPhrase = p.phrase.toLowerCase().includes(query);
        const matchMeaning = p.meaning.toLowerCase().includes(query);
        const matchExample = p.example.toLowerCase().includes(query);
        const matchTranslation = p.exampleTranslation && p.exampleTranslation.toLowerCase().includes(query);
        return matchPhrase || matchMeaning || matchExample || matchTranslation;
      });
    }

    return phrases;
  };

  const filteredPhrases = getFilteredPhrases();

  const getGradeLabel = (grade: GradeLevel) => {
    return `${grade}年级`;
  };

  // Show test page
  if (viewMode === 'test') {
    return (
      <PhraseTestPage
        user={user || { id: 'default', name: 'User', age: 8, avatar: '😊', level: 1, points: 0, badges: [], wordsLearned: [], lessonsCompleted: [], streakDays: 0, lastPracticeDate: '', createdAt: '', updatedAt: '' }}
        onExit={() => setViewMode('category')}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-3xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold mb-2">💬 短语学习</h2>
            <p className="text-white/80">
              共 {phrasesData.length} 个短语，当前筛选: {filteredPhrases.length} 个
            </p>
            {learnedPhrases.size > 0 && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full">
                <span>🎉</span>
                <span className="font-medium">已掌握 {learnedPhrases.size} 个</span>
              </div>
            )}
          </div>
        </div>

        {/* Search Box */}
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="🔍 搜索短语 (中英文模糊搜索)"
            className="w-full px-6 py-4 pr-12 rounded-2xl bg-white/95 backdrop-blur text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* View mode toggle */}
      <div className="flex gap-2 justify-center flex-wrap">
        <button
          onClick={() => setViewMode('test')}
          className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold hover:opacity-90"
        >
          🎯 开始测试
        </button>
        <button
          onClick={() => setViewMode('category')}
          className={`px-6 py-3 rounded-2xl font-medium transition-all ${
            viewMode === 'category'
              ? 'bg-primary text-white shadow-lg scale-105'
              : 'bg-white/50 text-gray-600 hover:bg-white/80'
          }`}
        >
          <span className="mr-2">📚</span>
          按分类学习
        </button>
        <button
          onClick={() => setViewMode('grade')}
          className={`px-6 py-3 rounded-2xl font-medium transition-all ${
            viewMode === 'grade'
              ? 'bg-primary text-white shadow-lg scale-105'
              : 'bg-white/50 text-gray-600 hover:bg-white/80'
          }`}
        >
          <span className="mr-2">📖</span>
          按年级学习
        </button>
        <button
          onClick={() => setViewMode('all')}
          className={`px-6 py-3 rounded-2xl font-medium transition-all ${
            viewMode === 'all'
              ? 'bg-primary text-white shadow-lg scale-105'
              : 'bg-white/50 text-gray-600 hover:bg-white/80'
          }`}
        >
          <span className="mr-2">📝</span>
          全部短语
        </button>
      </div>

      {/* Category/Grade selector */}
      {viewMode === 'category' && (
        <div className="bg-white rounded-2xl p-4 shadow-soft">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-800">🎯 快速选择分类</h3>
            {searchQuery && (
              <span className="text-sm text-gray-500">
                在筛选结果中找到 <span className="font-bold text-green-600">{filteredPhrases.length}</span> 个短语
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              全部
            </button>
            {phraseCategories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span>{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'grade' && (
        <div className="bg-white rounded-2xl p-4 shadow-soft">
          <h3 className="text-lg font-bold text-gray-800 mb-3">选择年级</h3>
          <div className="flex gap-2 flex-wrap">
            {(['3', '4', '5', '6', '7', '8', '9'] as GradeLevel[]).map(grade => (
              <button
                key={grade}
                onClick={() => setSelectedGrade(grade)}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  selectedGrade === grade
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getGradeLabel(grade)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Phrases count */}
      <div className="text-center text-gray-600">
        共找到 <span className="font-bold text-primary">{filteredPhrases.length}</span> 个短语
      </div>

      {/* Phrases list */}
      {filteredPhrases.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredPhrases.map(phrase => (
            <PhraseCard
              key={phrase.id}
              phrase={phrase}
              onLearned={learnedPhrases.has(phrase.id) ? undefined : handleLearned}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl shadow-soft">
          <div className="text-6xl mb-4">🔍</div>
          <p className="text-gray-500 text-lg">没有找到相关短语</p>
          <p className="text-gray-400 text-sm mt-2">请尝试其他分类或年级</p>
        </div>
      )}

      {/* Tips */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
          <span className="text-2xl">💡</span>
          学习小贴士
        </h3>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>点击发音按钮，听美式和英式发音，注意区别</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>重点理解"使用场景"，知道什么时候用这个短语</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>高频短语（红色标签）是日常最常用的，要优先掌握</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>大声朗读例句，加深记忆</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>尝试在日常对话中使用学到的短语</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
